import { func, object, string } from 'prop-types';
import classNames from 'classnames';
import React from 'react';

import { FormattedMessage, intlShape } from '../../util/reactIntl';
import {
  hasPublicPackageFamily,
  normalizePackages,
  packagePriceLabel,
  publishedMembers,
} from '../../util/listingPackages';
import { propTypes } from '../../util/types';

import css from './PackagePicker.module.css';

export const PACKAGE_PICKER_HASH = 'package-picker';

const listingsMapFrom = (listing, listingsById = {}) => {
  const currentId = listing?.id?.uuid;
  if (currentId && !listingsById[currentId]) {
    return { ...listingsById, [currentId]: listing };
  }
  return listingsById;
};

const PackageRowCopy = ({ item, price }) => (
  <>
    <span className={css.copy}>
      <span className={css.name}>{item.name}</span>
      {item.description ? <span className={css.description}>{item.description}</span> : null}
    </span>
    <span className={css.price}>{price}</span>
  </>
);

/**
 * Customer package ladder. Renders nothing unless two published members exist.
 */
const PackagePicker = props => {
  const {
    listing,
    listingsById,
    selectedListingId,
    onSelect,
    intl,
    config,
    className,
    headingClassName,
  } = props;

  const packages = normalizePackages(listing?.attributes?.publicData?.packages);
  const byId = listingsMapFrom(listing, listingsById);
  if (!packages || !hasPublicPackageFamily(listing, byId)) {
    return null;
  }

  const members = publishedMembers(packages.items, byId);
  const selectedId = selectedListingId || listing?.id?.uuid;

  return (
    <div className={classNames(css.root, className)} data-testid="package-picker">
      <h2
        className={classNames(css.heading, headingClassName)}
        data-testid="package-picker-heading"
      >
        <FormattedMessage id="PackagePicker.heading" />
      </h2>
      <ul className={css.list} role="list">
        {members.map(item => {
          const memberListing = byId[item.listingId];
          const selected = item.listingId === selectedId;
          const price = packagePriceLabel(memberListing, intl, config);
          return (
            <li key={item.id}>
              {selected ? (
                <div
                  className={classNames(css.row, css.rowSelected, css.rowCurrent)}
                  data-testid={`package-option-${item.listingId}`}
                  aria-current="true"
                >
                  <PackageRowCopy item={item} price={price} />
                </div>
              ) : (
                <button
                  type="button"
                  className={css.row}
                  data-testid={`package-option-${item.listingId}`}
                  onClick={() => {
                    if (typeof onSelect === 'function') {
                      onSelect(item.listingId);
                    }
                  }}
                >
                  <PackageRowCopy item={item} price={price} />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

PackagePicker.defaultProps = {
  listing: null,
  listingsById: {},
  selectedListingId: null,
  onSelect: null,
  className: null,
  headingClassName: null,
};

PackagePicker.propTypes = {
  listing: propTypes.listing,
  listingsById: object,
  selectedListingId: string,
  onSelect: func,
  intl: intlShape.isRequired,
  config: object,
  className: string,
  headingClassName: string,
};

export default PackagePicker;
